import { Button, Loader, styled, Table } from "@monolith-forensics/monolith-ui";
import React from "react";
import { useRelayInfo } from "../../../hooks";
import { useSnackbar } from "notistack";
import { useQuery } from "@tanstack/react-query";
import RelayAPI from "../../../../../../api/RelayAPI";
import { UserInvite } from "@/Features";

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  .user-name {
    font-weight: 500;
    color: ${(props) => props.theme.palette.primary.main};
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  }
`;

export const AccessRequests: React.FC = () => {
  const { relayInfo } = useRelayInfo();
  const { enqueueSnackbar } = useSnackbar();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["relay:user-invites", { tenant_uuid: relayInfo?.tenant_uuid }],
    queryFn: () =>
      RelayAPI.getUserInvites({ tenant_uuid: relayInfo?.tenant_uuid }),
    enabled: !!relayInfo,
  });

  const handleGrantAccess = (data: {
    invite_uuid: string;
    tenant_uuid: string;
  }) => {
    RelayAPI.grantUserAccess({
      invite_uuid: data.invite_uuid,
      tenant_uuid: data.tenant_uuid,
    }).then((res) => {
      handleQueryRefetch();

      enqueueSnackbar("Relay Access Granted", {
        variant: "success",
      });
    });
  };

  const handleDenyAccess = (data: {
    invite_uuid: string;
    tenant_uuid: string;
  }) => {
    RelayAPI.denyUserAccess({
      invite_uuid: data.invite_uuid,
      tenant_uuid: data.tenant_uuid,
    }).then((res) => {
      handleQueryRefetch();

      enqueueSnackbar("Relay Access Denied", {
        variant: "success",
      });
    });
  };

  const handleDeleteInvite = (data: {
    invite_uuid: string;
    tenant_uuid: string;
  }) => {
    RelayAPI.denyUserAccess({
      invite_uuid: data.invite_uuid,
      tenant_uuid: data.tenant_uuid,
    }).then((res) => {
      handleQueryRefetch();

      enqueueSnackbar("Relay Invite Revoked", {
        variant: "success",
      });
    });
  };

  const handleQueryRefetch = async () => {
    refetch();
  };

  if (isLoading) return <Loader />;

  let users =
    data?.data?.map((invite: UserInvite) => {
      return {
        ...invite,
        ...invite.user,
        email: invite?.user?.email || invite.invite_email,
      };
    }) || [];

  return (
    <StyledContainer>
      <Table
        data={users ? users : []}
        keyField="invite_uuid"
        columnProps={[
          {
            dataField: "full_name",
            caption: "Name",
          },
          {
            dataField: "email",
            caption: "Email",
          },
          {
            dataField: "org",
            caption: "Organization",
          },
          {
            dataField: "status",
            caption: "Status",
            render: ({ rowData }) => {
              return (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 10,
                    alignContent: "center",
                  }}
                >
                  {rowData.type === "invite" ? (
                    <>
                      <div>Invited</div>
                      <Button
                        variant="text"
                        color="error"
                        size="xs"
                        style={{ height: "fit-content" }}
                        onClick={() => handleDeleteInvite(rowData)}
                      >
                        Revoke
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="text"
                        color="primary"
                        size="xs"
                        style={{ height: "fit-content" }}
                        onClick={() => handleGrantAccess(rowData)}
                      >
                        Grant Access
                      </Button>
                      <Button
                        variant="text"
                        color="error"
                        size="xs"
                        style={{ height: "fit-content" }}
                        onClick={() => handleDenyAccess(rowData)}
                      >
                        Deny Access
                      </Button>
                    </>
                  )}
                </div>
              );
            },
          },
        ]}
      ></Table>
    </StyledContainer>
  );
};
